import './style/salesDashboardTable.scss';
import {AwesomeIcon, ChangeFunction, Loader, ResponseData, roundToNumber} from '@dvrd/dvr-controls';
import {Icon} from '@iconify/react';
import classNames from 'classnames';
import React, {MouseEventHandler, useContext, useEffect, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {SalesCategory, SalesColumn} from '../../util/enums';
import {getOpenProduct} from '../../util/requestSender';
import {
    GeneralSalesProduct,
    PortalSelectOption,
    SalesDashboardFilter,
    SalesDashboardFilterGroup,
    SalesDashboardStats
} from '../../util/types';
import {formatCurrency, formatNumberSign, parseThousandSeparator, renderPlural} from '../../util/utils';
import PortalSelectController from '../controls/portalSelect/portalSelectController';
import {AppContext} from '../misc/appContext';
import SalesFilterComponent from './salesFilterComponent';
import {BluemateTooltip} from '../controls';
import {
    allSalesDashboardColumns,
    salesCategoryIsVisible,
    salesDashboardColumnMap
} from '../../util/salesDashboardUtil.tsx';

interface Props {
    onChangeOrder: (order: SalesColumn) => MouseEventHandler;
    onApplyFilters: (filterGroup: SalesDashboardFilterGroup) => void;
    onClickProduct: (product: GeneralSalesProduct | null) => MouseEventHandler;
    onEditCosts: (product: GeneralSalesProduct) => void;
    onChangeLimit: ChangeFunction<number>;
    onChangePage: (page: number) => MouseEventHandler;
    filters: Array<SalesDashboardFilterGroup>;
    total: number;
    products: Array<GeneralSalesProduct>;
    order: SalesColumn;
    loadingProducts: boolean;
    limit: number;
    page: number;
    visibleColumns: Set<SalesColumn>;
    comparisonVisible: boolean;
}

function useColumnLabelMap(): Record<SalesColumn, string> {
    const {t} = useTranslation();
    return {
        [SalesColumn.TITLE]: t('salesDashboardV2.table.product', 'Product'),
        [SalesColumn.QUANTITY]: t('salesDashboardV2.table.quantitySold', 'Aantal verkocht'),
        [SalesColumn.REVENUE]: t('salesDashboardV2.table.revenue', 'Omzet'),
        [SalesColumn.RETURN_QUANTITY]: t('salesDashboardV2.table.returnQuantity', 'Aantal retour'),
        [SalesColumn.RETURN_REVENUE]: t('salesDashboardV2.table.returnRevenue', 'Retour omzet'),
        [SalesColumn.RETURN_COSTS]: t('salesDashboardV2.table.returnCosts', 'Retour kosten'),
        [SalesColumn.RETURN_COMMISSION]: t('salesDashboardV2.table.returnCommission', 'Retour commissie'),
        [SalesColumn.ORGANIC_SALES]: t('salesDashboardV2.table.organicSold', 'Organisch verkocht'),
        [SalesColumn.AD_SPENT]: t('salesDashboardV2.table.adCosts', 'Advertentie kosten'),
        [SalesColumn.SPONSORED_SALES]: t('salesDashboardV2.table.adRevenue', 'Advertentie omzet'),
        [SalesColumn.ACOS]: 'ACoS',
        [SalesColumn.CPC]: 'CPC',
        [SalesColumn.PROD_COSTS]: t('salesDashboardV2.table.productCosts', 'Productkosten'),
        [SalesColumn.COMMISSION]: t('salesDashboardV2.table.commission', 'Commissie'),
        [SalesColumn.VAT]: t('salesDashboardV2.table.vat', 'BTW'),
        [SalesColumn.PROFIT]: t('salesDashboardV2.table.profit', 'Winst'),
        [SalesColumn.MARGIN]: t('salesDashboardV2.table.marginPct', 'Marge %'),
        [SalesColumn.STOCK]: t('salesDashboardV2.table.stock', 'Voorraad')
    };
}

const columnIndexes: Record<SalesColumn, number> = {
    [SalesColumn.TITLE]: -1,
    [SalesColumn.QUANTITY]: 0,
    [SalesColumn.REVENUE]: 1,
    [SalesColumn.ORGANIC_SALES]: 2,
    [SalesColumn.PROFIT]: 3,
    [SalesColumn.MARGIN]: 4,
    [SalesColumn.PROD_COSTS]: 5,
    [SalesColumn.COMMISSION]: 6,
    [SalesColumn.VAT]: 7,
    [SalesColumn.AD_SPENT]: 8,
    [SalesColumn.SPONSORED_SALES]: 9,
    [SalesColumn.ACOS]: 10,
    [SalesColumn.CPC]: 11,
    [SalesColumn.RETURN_QUANTITY]: 12,
    [SalesColumn.RETURN_REVENUE]: 13,
    [SalesColumn.RETURN_COSTS]: 14,
    [SalesColumn.RETURN_COMMISSION]: 15,
    [SalesColumn.STOCK]: 16
};

const limitItems: Array<PortalSelectOption<number>> = [
    {value: 5, label: '5'},
    {value: 10, label: '10'},
    {value: 25, label: '25'},
    {value: 50, label: '50'}
];

interface RowProps {
    onClickProduct: (product: GeneralSalesProduct | null) => MouseEventHandler;
    onEditCosts: (product: GeneralSalesProduct) => void;
    handleCellMouseEnter: MouseEventHandler;
    handleCellMouseLeave: MouseEventHandler;
    product: GeneralSalesProduct;
    visibleColumns: Set<SalesColumn>;
    comparisonVisible: boolean;
}

type Comparison = {
    percentage: string;
    neutralComparison: boolean;
    positiveComparison: boolean;
    comparingValue: number | string;
};

function getComparison(
    product: GeneralSalesProduct,
    key: keyof Omit<SalesDashboardStats, 'comparison'>,
    isCurrency: boolean = true
): Comparison {
    const value = product[key];
    if (!product.comparison || value === null)
        return {
            percentage: value === 0 ? '+0.0' : '+100.0',
            neutralComparison: value === null || value === 0,
            positiveComparison: value !== null && value > 0,
            comparingValue: isCurrency ? formatCurrency(0) : 0
        };
    let comparingValue: string | number | null = product.comparison[key];
    if (comparingValue === null)
        return {
            percentage: value === 0 ? '+0.0' : '+100.0',
            neutralComparison: value === 0,
            positiveComparison: value > 0,
            comparingValue: isCurrency ? formatCurrency(0) : 0
        };
    let percentage: number;
    let neutralComparison: boolean = false;
    let positiveComparison: boolean = false;
    if (comparingValue === value) {
        percentage = 0;
        neutralComparison = true;
    } else if (comparingValue === 0) {
        percentage = 100;
        positiveComparison = true;
    } else {
        percentage = ((value - comparingValue) / comparingValue) * 100;
        positiveComparison = percentage > 0;
    }
    if (isCurrency) comparingValue = formatCurrency(comparingValue);
    else comparingValue = comparingValue.toString().replace('.', ',');
    return {
        percentage: formatNumberSign(roundToNumber(percentage, 1)),
        positiveComparison: positiveComparison,
        neutralComparison: neutralComparison,
        comparingValue: comparingValue
    };
}

function TableRow(props: RowProps) {
    const {t} = useTranslation();
    const {account} = useContext(AppContext).accountContext;
    const {onClickProduct, handleCellMouseLeave, handleCellMouseEnter, visibleColumns, comparisonVisible} = props;
    const [product, setProduct] = useState<GeneralSalesProduct>(props.product);
    const abortController = useRef<AbortController | null>(null);

    function onClickEditCosts(evt: React.MouseEvent) {
        evt.stopPropagation();
        props.onEditCosts(product);
    }

    function loadOpenData(product: GeneralSalesProduct) {
        if (!account || !!product.image_url) return;
        abortController.current = getOpenProduct({
            ean: product.ean,
            account_id: account.id,
            callback: (data: ResponseData) => {
                if (data.success) {
                    const openProduct = data.open_product;
                    setProduct({...product, image_url: openProduct.image_url, global_id: openProduct.global_id});
                }
            }
        });
    }

    function renderCell(
        label: string | number,
        category: SalesCategory,
        column: SalesColumn,
        comparisonKey: keyof Omit<SalesDashboardStats, 'comparison' | 'hidden'>,
        isCurrency: boolean,
        config?: {
            noComparison?: boolean;
            invertComparison?: boolean;
            onCog?: MouseEventHandler;
            useThousandSeparator?: boolean;
            info?: string;
        }
    ) {
        const columnIndex = columnIndexes[column];
        const comparison = getComparison(product, comparisonKey, isCurrency);
        const columnIsHidden = !visibleColumns.has(column);
        if (config?.useThousandSeparator) label = parseThousandSeparator(label);
        return (
            <td
                data-col={columnIndex}
                data-category={category}
                data-column={column}
                onMouseEnter={handleCellMouseEnter}
                onMouseLeave={handleCellMouseLeave}
                onClick={config?.onCog}
                className={classNames(
                    'cell product-cell',
                    config?.onCog && 'editable',
                    columnIsHidden && 'column-hidden'
                )}
            >
                <div className='cell-content'>
                    {label}
                    {!config?.noComparison && renderCellComparison(comparison, config)}
                    {config?.onCog && (
                        <BluemateTooltip text={t('edit', 'Aanpassen')}>
                            <AwesomeIcon name='cog' className='cog-icon' />
                        </BluemateTooltip>
                    )}
                    {config?.info && (
                        <div className='cell-info-container'>
                            <AwesomeIcon name='info-circle' className='info-icon' />
                            <p className='cell-info left'>{config.info}</p>
                        </div>
                    )}
                </div>
            </td>
        );
    }

    function renderCellComparison(
        comparison: Comparison,
        config?: {
            invertComparison?: boolean;
            useThousandSeparator?: boolean;
        }
    ) {
        const {percentage, neutralComparison, positiveComparison} = comparison;
        let {comparingValue} = comparison;
        if (config?.useThousandSeparator) comparingValue = parseThousandSeparator(comparingValue);
        const isPositive = config?.invertComparison ? !positiveComparison : positiveComparison;
        return (
            <div
                className={classNames(
                    'comparison',
                    !comparisonVisible && 'category-hidden',
                    neutralComparison ? 'neutral' : isPositive ? 'positive' : 'negative'
                )}
            >
                <span className='comparison-percentage'>{percentage}%</span>
                <span className='comparison-value'>
                    {t('salesDashboardV2.table.previous', 'Vorige')}: {comparingValue}
                </span>
            </div>
        );
    }

    function abortLoad() {
        abortController.current?.abort();
        abortController.current = null;
    }

    useEffect(() => {
        setProduct(props.product);
        loadOpenData(props.product);

        return function () {
            abortLoad();
        };
    }, [props.product]);

    const {
        image_url,
        title,
        ean,
        quantity,
        revenue_excl,
        organic_sales,
        ad_cost,
        sponsored_sales,
        acos,
        cpc,
        costs,
        commission,
        profit,
        margin,
        return_quantity,
        returned_revenue_excl,
        returned_commission,
        return_costs,
        stock,
        vat_amount
    } = product;

    return (
        <tr className='product-row' onClick={onClickProduct(product)}>
            <td className='cell stick-left'>
                <div className='product-info'>
                    {!image_url ? (
                        <AwesomeIcon name='image' className='product-image icon' />
                    ) : (
                        <img src={image_url} className='product-image' alt={title ?? ean} loading='lazy' />
                    )}
                    <div className='product-title-container'>
                        <div className='title-row'>
                            <label className='product-title'>{title}</label>
                            <div className='icon-btn'>
                                <Icon icon='mdi:bar-chart' />
                            </div>
                        </div>
                        <label className='product-ean'>EAN: {ean}</label>
                    </div>
                </div>
            </td>
            {renderCell(quantity, SalesCategory.SALES, SalesColumn.QUANTITY, 'quantity', false, {
                useThousandSeparator: true
            })}
            {renderCell(formatCurrency(revenue_excl), SalesCategory.SALES, SalesColumn.REVENUE, 'revenue_excl', true)}
            {renderCell(
                formatCurrency(organic_sales),
                SalesCategory.SALES,
                SalesColumn.ORGANIC_SALES,
                'organic_sales',
                true
            )}
            {renderCell(formatCurrency(profit), SalesCategory.PROFIT, SalesColumn.PROFIT, 'profit', true, {
                noComparison: profit === null
            })}
            {renderCell(
                margin === null ? '-' : `${margin} %`,
                SalesCategory.PROFIT,
                SalesColumn.MARGIN,
                'margin',
                false,
                {noComparison: margin === null}
            )}
            {renderCell(formatCurrency(costs), SalesCategory.COSTS, SalesColumn.PROD_COSTS, 'costs', true, {
                noComparison: true,
                onCog: onClickEditCosts
            })}
            {renderCell(formatCurrency(commission), SalesCategory.COSTS, SalesColumn.COMMISSION, 'commission', true, {
                invertComparison: true
            })}
            {renderCell(formatCurrency(vat_amount), SalesCategory.COSTS, SalesColumn.VAT, 'vat_amount', true, {
                invertComparison: true
            })}
            {renderCell(formatCurrency(ad_cost), SalesCategory.ADVERTISING, SalesColumn.AD_SPENT, 'ad_cost', true, {
                invertComparison: true
            })}
            {renderCell(
                formatCurrency(sponsored_sales),
                SalesCategory.ADVERTISING,
                SalesColumn.SPONSORED_SALES,
                'sponsored_sales',
                true
            )}
            {renderCell(acos, SalesCategory.ADVERTISING, SalesColumn.ACOS, 'acos', false, {invertComparison: true})}
            {renderCell(formatCurrency(cpc), SalesCategory.ADVERTISING, SalesColumn.CPC, 'cpc', true, {
                invertComparison: true
            })}
            {renderCell(return_quantity, SalesCategory.RETURNS, SalesColumn.RETURN_QUANTITY, 'return_quantity', false, {
                invertComparison: true,
                useThousandSeparator: true
            })}
            {renderCell(
                formatCurrency(returned_revenue_excl),
                SalesCategory.RETURNS,
                SalesColumn.RETURN_REVENUE,
                'returned_revenue_excl',
                true,
                {invertComparison: true}
            )}
            {renderCell(
                formatCurrency(return_costs),
                SalesCategory.RETURNS,
                SalesColumn.RETURN_COSTS,
                'return_costs',
                true,
                {invertComparison: true}
            )}
            {renderCell(
                formatCurrency(returned_commission),
                SalesCategory.RETURNS,
                SalesColumn.RETURN_COMMISSION,
                'returned_commission',
                true,
                {invertComparison: true}
            )}
            {renderCell(stock, SalesCategory.STOCK, SalesColumn.STOCK, 'stock', false, {
                noComparison: true,
                useThousandSeparator: true,
                info: t(
                    'salesDashboardV2.table.stockInfo',
                    'Huidige voorraad van het product, onafhankelijk van de geselecteerde periode.'
                )
            })}
        </tr>
    );
}

export default function SalesDashboardTable(props: Props) {
    const {t} = useTranslation();
    const columnLabelMap = useColumnLabelMap();
    const {
        onChangeOrder,
        onClickProduct,
        onEditCosts,
        total,
        products,
        order,
        loadingProducts,
        onChangeLimit,
        limit,
        onChangePage,
        page,
        visibleColumns,
        comparisonVisible
    } = props;
    const [openFilters, setOpenFilters] = useState<Set<SalesColumn>>(new Set());
    const tableRef = React.useRef<HTMLTableElement>(null);

    function onToggleFilterState(column: SalesColumn, open: boolean) {
        const _openFilters = new Set(openFilters);
        if (open) _openFilters.add(column);
        else _openFilters.delete(column);
        setOpenFilters(_openFilters);
    }

    function renderTableHead() {
        return (
            <thead>
                <tr>
                    <th className='cell head-main stick-left stick-top on-top'>
                        <div className='product-info'>
                            <label className='category-label hidden'>
                                {t('salesDashboardV2.table.sales', 'Verkopen')}
                            </label>
                        </div>
                    </th>
                    {renderSalesCategory()}
                    {renderProfitCategory()}
                    {renderCostsCategory()}
                    {renderAdvertisingCategory()}
                    {renderReturnsCategory()}
                    {renderStockCategory()}
                </tr>
                <tr>
                    <th className='cell head-column stick-left stick-top on-top align-left'>
                        <label className='column-label'>
                            {total}{' '}
                            {renderPlural(
                                total,
                                t('salesDashboardV2.table.productSingular', 'product'),
                                t('salesDashboardV2.table.productPlural', 'producten')
                            )}
                            <div className='icon-btn' onClick={onClickProduct(null)}>
                                <Icon icon='mdi:bar-chart' />
                            </div>
                        </label>
                        <div className={classNames('column-actions', order === SalesColumn.TITLE && 'keep-open')}>
                            <AwesomeIcon
                                name='arrow-right-arrow-left'
                                className={classNames('order-icon', order === SalesColumn.TITLE && 'active')}
                                onClick={onChangeOrder(SalesColumn.TITLE)}
                            />
                        </div>
                    </th>
                    {/* SALES */}
                    {renderColumnHeaderCell(SalesColumn.QUANTITY)}
                    {renderColumnHeaderCell(SalesColumn.REVENUE)}
                    {renderColumnHeaderCell(SalesColumn.ORGANIC_SALES)}
                    {/* PROFIT */}
                    {renderColumnHeaderCell(SalesColumn.PROFIT)}
                    {renderColumnHeaderCell(SalesColumn.MARGIN)}
                    {/* COSTS */}
                    {renderColumnHeaderCell(SalesColumn.PROD_COSTS)}
                    {renderColumnHeaderCell(SalesColumn.COMMISSION)}
                    {renderColumnHeaderCell(SalesColumn.VAT)}
                    {/* ADVERTISING */}
                    {renderColumnHeaderCell(SalesColumn.AD_SPENT)}
                    {renderColumnHeaderCell(SalesColumn.SPONSORED_SALES)}
                    {renderColumnHeaderCell(SalesColumn.ACOS)}
                    {renderColumnHeaderCell(SalesColumn.CPC)}
                    {/* RETURNS */}
                    {renderColumnHeaderCell(SalesColumn.RETURN_QUANTITY)}
                    {renderColumnHeaderCell(SalesColumn.RETURN_REVENUE)}
                    {renderColumnHeaderCell(SalesColumn.RETURN_COSTS)}
                    {renderColumnHeaderCell(SalesColumn.RETURN_COMMISSION)}
                    {/* STOCK */}
                    {renderColumnHeaderCell(SalesColumn.STOCK, {
                        info: t(
                            'salesDashboardV2.table.stockInfo',
                            'Huidige voorraad van het product, onafhankelijk van de geselecteerde periode.'
                        )
                    })}
                </tr>
            </thead>
        );
    }

    function renderSalesCategory() {
        const category = SalesCategory.SALES;
        return renderMainHeaderCell(category, t('salesDashboardV2.table.sales', 'Verkopen'), 'green');
    }

    function renderStockCategory() {
        const category = SalesCategory.STOCK;
        return renderMainHeaderCell(category, t('salesDashboardV2.table.stock', 'Voorraad'), 'yellow');
    }

    function renderAdvertisingCategory() {
        const category = SalesCategory.ADVERTISING;
        return renderMainHeaderCell(category, t('salesDashboardV2.table.advertising', 'Adverteren'), 'purple');
    }

    function renderCostsCategory() {
        const category = SalesCategory.COSTS;
        return renderMainHeaderCell(category, t('salesDashboardV2.table.costs', 'Kosten'), 'blue');
    }

    function renderProfitCategory() {
        const category = SalesCategory.PROFIT;
        return renderMainHeaderCell(category, t('salesDashboardV2.table.profit', 'Winst'), 'orange');
    }

    function renderReturnsCategory() {
        const category = SalesCategory.RETURNS;
        return renderMainHeaderCell(category, t('salesDashboardV2.table.returns', 'Retouren'), 'red');
    }

    function renderMainHeaderCell(category: SalesCategory, label: string, color: string) {
        const visible = salesCategoryIsVisible(category, comparisonVisible, visibleColumns);
        const categoryColumns = salesDashboardColumnMap[category];
        const visibleCategoryColumns = categoryColumns.filter((col: SalesColumn) => visibleColumns.has(col)).length;
        return (
            <th
                colSpan={visibleCategoryColumns}
                className={classNames('cell head-main stick-top', color, !visible && 'category-hidden')}
            >
                <label className='category-label'>{label}</label>
            </th>
        );
    }

    function renderColumnHeaderCell(column: SalesColumn, config?: {info?: string}) {
        const label = columnLabelMap[column];
        const filterGroup = props.filters.find(
            (filterGroup: SalesDashboardFilterGroup) => filterGroup.field === column
        )!;
        const index = allSalesDashboardColumns.indexOf(column);
        const hasActiveFilter = filterGroup.filters.some((filter: SalesDashboardFilter) => filter.valueSet);
        const keepOpen = openFilters.has(column) || order === column || hasActiveFilter;
        const columnIsHidden = !visibleColumns.has(column);
        return (
            <th
                className={classNames('cell head-column stick-top', columnIsHidden && 'column-hidden')}
                data-col={index}
            >
                <label className='column-label'>{label}</label>
                <div className={classNames('column-actions', keepOpen && 'keep-open')}>
                    <AwesomeIcon
                        name='arrow-right-arrow-left'
                        className={classNames('order-icon', order === column && 'active')}
                        onClick={onChangeOrder(column)}
                    />
                    <SalesFilterComponent
                        {...props}
                        onToggleFilterState={onToggleFilterState}
                        filterGroup={filterGroup}
                        column={column}
                    />
                    {config?.info && (
                        <div className='cell-info-container'>
                            <AwesomeIcon name='info-circle' className='info-icon' />
                            <p className='cell-info left'>{config.info}</p>
                        </div>
                    )}
                </div>
            </th>
        );
    }

    function renderTableBody() {
        return (
            <tbody>
                {!products.length && (
                    <tr className='table-placeholder-row'>
                        <td colSpan={999}>
                            <div className='table-placeholder'>
                                <Icon icon='mdi:magnify-close' className='empty-icon' />
                                <div className='empty-title'>
                                    {t('salesDashboardV2.table.noProducts', 'Geen producten gevonden')}
                                </div>
                                <div className='empty-text'>
                                    {t(
                                        'salesDashboardV2.table.noProductsHint',
                                        'Pas je filters aan of voeg zoekwoorden toe.'
                                    )}
                                </div>
                            </div>
                        </td>
                    </tr>
                )}
                {products.map(renderProduct)}
            </tbody>
        );
    }

    function renderProduct(product: GeneralSalesProduct, index: number) {
        return (
            <TableRow
                key={index}
                onClickProduct={onClickProduct}
                onEditCosts={onEditCosts}
                handleCellMouseEnter={handleCellMouseEnter}
                handleCellMouseLeave={handleCellMouseLeave}
                product={product}
                visibleColumns={visibleColumns}
                comparisonVisible={comparisonVisible}
            />
        );
    }

    function handleCellMouseEnter(e: React.MouseEvent<HTMLTableCellElement>) {
        tableRef.current?.setAttribute('data-hover-col', e.currentTarget.dataset.col!);
    }

    function handleCellMouseLeave() {
        tableRef.current?.removeAttribute('data-hover-col');
    }

    function renderPagination() {
        const maxPage = Math.ceil(total / limit);
        const currentPage = page + 1;
        let firstPage: number;
        if (currentPage <= 3) firstPage = 1;
        else if (currentPage > maxPage - 2) firstPage = maxPage - 4;
        else firstPage = currentPage - 2;
        const pages: Array<number> = [];
        for (let ix = firstPage; ix <= maxPage && pages.length < 5; ix++) pages.push(ix);
        const prevDisabled = currentPage === 1;
        const nextDisabled = currentPage >= maxPage;
        const firstRow = Math.min(total, limit * page + 1);
        const lastRow = Math.min(total, limit * (page + 1));
        return (
            <div className='pagination-container'>
                <div className='rows-container'>
                    <PortalSelectController
                        onChange={(value: number | null) => onChangeLimit(value ?? 10)}
                        value={limit}
                        items={limitItems}
                        label={t('salesDashboardV2.table.rowsPerPage', 'Rijen per pagina')}
                        placeholder={String(limit)}
                        itemsPosition='top'
                        size='small'
                        shape='rounded'
                    />
                </div>
                <div className='pagination-info'>
                    {total > 0
                        ? t('salesDashboardV2.table.paginationRange', '{{firstRow}}\u2013{{lastRow}} van {{total}}', {
                              firstRow,
                              lastRow,
                              total
                          })
                        : t('salesDashboardV2.table.noResults', '0 resultaten')}
                </div>
                <div className='pages-container'>
                    <div
                        className={classNames('page-icon-container', prevDisabled && 'disabled')}
                        onClick={prevDisabled ? undefined : onChangePage(page - 1)}
                    >
                        <AwesomeIcon name='chevron-left' className='page-icon' />
                    </div>
                    {pages.map((page: number) => renderPage(page, page === currentPage))}
                    <div
                        className={classNames('page-icon-container', nextDisabled && 'disabled')}
                        onClick={nextDisabled ? undefined : onChangePage(page + 1)}
                    >
                        <AwesomeIcon name='chevron-right' className='page-icon' />
                    </div>
                </div>
            </div>
        );
    }

    function renderPage(page: number, isActive: boolean) {
        return (
            <div className={classNames('pager', isActive && 'active')} onClick={onChangePage(page - 1)}>
                <span key={page} className='page-number'>
                    {page}
                </span>
            </div>
        );
    }

    return (
        <div className='sales-dashboard-table-container'>
            <Loader active={loadingProducts} />
            <div className='table-container'>
                <table ref={tableRef} className='sales-dashboard-table'>
                    {renderTableHead()}
                    {renderTableBody()}
                </table>
            </div>
            {renderPagination()}
        </div>
    );
}
